import { Link, useParams } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleGridCard from "@/components/feature/ArticleGridCard";
import TopicCard from "@/components/feature/TopicCard";
import EmptyState from "@/components/feature/EmptyState";
import { getArticleById, getTopicArticles, getTopicById } from "@/utils/news";
import { topics } from "@/mocks/topics";
import type { Topic } from "@/mocks/topics";

export default function TopicPage() {
  const { topicId } = useParams();
  const topic = getTopicById(topicId);

  usePageMeta({
    title: topic ? `${topic.title} - 黑料吃瓜网专题` : "专题不存在 - 黑料吃瓜网",
    description: topic
      ? `${topic.intro.slice(0, 60)} 黑料吃瓜网「${topic.title}」专题，收录相关报道与深度观察。`
      : "该专题不存在或已下线，返回首页浏览其他娱乐八卦内容。",
    keywords: topic ? `黑料吃瓜网,${topic.title},${topic.subtitle}` : "黑料吃瓜网,专题",
  });

  if (!topic) {
    return (
      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-bookmark-3-line"
          title="这个专题不存在"
          description="专题可能已经下线或链接有误。你可以返回首页，或者从热门专题中挑选感兴趣的内容。"
          actionLabel="查看热门专题"
          actionTo="/topic/topic-drama-2026"
        />
      </div>
    );
  }

  const articles = getTopicArticles(topic);
  const related = topic.relatedTopicIds
    .map((id) => topics.find((item) => item.id === id))
    .filter((item): item is Topic => Boolean(item));

  return (
    <>
      <section className="relative w-full h-[280px] sm:h-[340px] md:h-[420px] bg-background-800">
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title} - 黑料吃瓜网专题`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/92 via-foreground-950/55 to-foreground-950/20" />
        <div className="absolute inset-0">
          <div className="w-full max-w-[1280px] mx-auto h-full px-4 md:px-6 flex flex-col justify-end pb-6 md:pb-10">
            <div className="flex items-center gap-2 text-xs text-background-400 mb-3">
              <Link to="/" className="hover:text-background-100 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line" />
              <span>热门专题</span>
            </div>
            <span className="inline-flex w-fit items-center gap-1.5 px-2.5 py-1 rounded bg-accent-500 text-foreground-950 text-[11px] font-medium mb-3">
              <i className="ri-bookmark-3-fill" />
              专题 · {articles.length} 篇
            </span>
            <h1 className="font-heading text-2xl md:text-[42px] md:leading-tight font-bold text-background-50">
              {topic.title}
            </h1>
            <p className="mt-2 text-sm md:text-base text-accent-200">{topic.subtitle}</p>
            <p className="hidden sm:block mt-3 text-xs md:text-sm leading-relaxed text-background-300 max-w-3xl">
              {topic.intro}
            </p>
          </div>
        </div>
      </section>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-9">
        <Breadcrumb items={[{ label: "热门专题", to: "/topic/topic-drama-2026" }, { label: topic.title }]} />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-10 items-start">
          <div>
            <div className="bg-background-50 border-l-2 border-primary-500 rounded-r-lg p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-quote-text text-primary-500" />
                为什么推荐这个专题
              </h2>
              <p className="mt-2.5 text-[13px] md:text-sm leading-relaxed text-foreground-700">{topic.reason}</p>
            </div>

            <div className="mt-8 space-y-9 md:space-y-11">
              {topic.sections.map((section, index) => {
                const sectionArticles = section.newsIds
                  .map((id) => getArticleById(id))
                  .filter((item) => Boolean(item));
                if (sectionArticles.length === 0) return null;
                return (
                  <section key={section.title}>
                    <div className="flex items-start gap-3 mb-4">
                      <span className="shrink-0 w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center font-heading text-sm font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">
                          {section.title}
                        </h2>
                        <p className="mt-1 text-xs text-foreground-500">{section.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {sectionArticles.map((article) =>
                        article ? <ArticleGridCard key={article.id} article={article} /> : null,
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-[104px]">
            <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-list-check-2 text-primary-500" />
                专题目录
              </h2>
              <ul className="mt-3 space-y-2.5">
                {topic.sections.map((section) => (
                  <li key={section.title} className="flex items-start gap-2 text-xs text-foreground-600">
                    <i className="ri-corner-down-right-line text-foreground-400 mt-0.5" />
                    <span className="leading-relaxed">{section.title}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-links-line text-primary-500" />
                相关专题
              </h2>
              <div className="mt-3 space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/topic/${item.id}`}
                    className="group flex gap-3 cursor-pointer"
                  >
                    <div className="w-20 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title} - 黑料吃瓜网专题`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground-800 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </p>
                      <p className="mt-1 text-[11px] text-foreground-500 line-clamp-1">{item.subtitle}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-950 rounded-lg p-4 md:p-5">
              <h2 className="font-heading text-base font-bold text-background-50">想看别的？</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                搜索功能支持按标题、标签与正文关键词查找，也可以直接进入分类页按时间或热度浏览。
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to="/search"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-accent-500 text-foreground-950 text-xs whitespace-nowrap hover:bg-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line" />
                  去搜索
                </Link>
                <Link
                  to="/category/gossip"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-700 text-background-200 text-xs whitespace-nowrap hover:border-background-500 transition-colors cursor-pointer"
                >
                  明星绯闻
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="w-full bg-background-200/60 border-y border-background-300/50 py-9 md:py-12">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-5">
            <span className="w-1.5 h-5 rounded-full bg-primary-500" />
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950">更多专题</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {topics
              .filter((item) => item.id !== topic.id)
              .map((item) => (
                <TopicCard key={item.id} topic={item} />
              ))}
          </div>
        </div>
      </section>
    </>
  );
}